import { KnownAccessControls } from 'app/business-logic/security/KnownAccessControls';
import { NoiseEvent } from 'app/business-logic/services/noise-service';
import { AccessControlGate } from 'app/routing/access-control-gate';
import { useI18n } from 'core/hooks/useI18n';
import Guid from 'core/types/Guid';
import { useFormikContext } from 'formik';
import { ClassificationsWrapper, EventClassificationHeading } from '../EventClassification.styles';
import { EventClassificationState } from '../EventClassification.types';
import { ClassificationButton } from '../classification-button';
import { ClassifiedByAiTag } from '../classified-by-ai-tag';
import { useSetFormikFieldValue } from 'app/hooks/useSetFormikFieldValue';

export const EventClassificationButtons = ({ event, isLoading }: { event: NoiseEvent; isLoading: boolean }) => {
  const { values } = useFormikContext<EventClassificationState>();
  const setFormikFieldValue = useSetFormikFieldValue<EventClassificationState>();
  const setIsDisregarded = (isDisregarded: boolean) => {
    const key: keyof EventClassificationState = 'disregarded';
    const value: EventClassificationState[typeof key] = isDisregarded;
    setFormikFieldValue(key, value);
  };
  const { l10n } = useI18n('app/views/alerts', 'eventClassification');
  const selectClassification = ({ id, isSelected }: { id: Guid; isSelected: boolean }) => {
    if (isLoading) return;
    const field: keyof EventClassificationState = 'classifications';
    const value: EventClassificationState[typeof field] = values.classifications.map(c =>
      c.classification.id === id ? { ...c, isSelected } : c
    );
    const selectedClassifications = value.filter(({ isSelected }) => isSelected);
    const allSelectedAreDisregardable =
      selectedClassifications.length > 0 &&
      selectedClassifications.every(({ classification: { disregardEvents } }) => disregardEvents);
    const someSelectedAreNotDisregardable = selectedClassifications.some(
      ({ classification: { disregardEvents } }) => !disregardEvents
    );
    const noneSelected = selectedClassifications.length === 0;

    if (allSelectedAreDisregardable) setIsDisregarded(true);
    if (someSelectedAreNotDisregardable) setIsDisregarded(false);
    if (noneSelected) setIsDisregarded(false);
    setFormikFieldValue(field, value);
  };

  return (
    <AccessControlGate requires={KnownAccessControls.app.noise.classification.data.write._}>
      <EventClassificationHeading>{l10n('heading.eventClassification')}</EventClassificationHeading>
      <ClassificationsWrapper>
        {values.classifications.map(classification => (
          <ClassificationButton
            key={classification.classification.id}
            isLoading={isLoading}
            selectClassification={selectClassification}
            {...classification}
          />
        ))}
      </ClassificationsWrapper>
      {event.classifications.some(({ isAiClassified }) => isAiClassified) && <ClassifiedByAiTag />}
    </AccessControlGate>
  );
};
